<template>
  <div class="h-full height-table">
    <table class="w-full h-full">
      <thead class="border-b-2 border-zt-dark-200">
        <tr
          class="w-full h-16 flex justify-between items-center text-base text-zt-dark-300 px-8"
        >
          <th
            v-for="(column, index) in columnHeader"
            :class="['font-normal', column.className]"
            :style="{ width: column.width }"
            :key="index"
            v-if="!column.hidden"
          >
            {{ column.title }}
          </th>
        </tr>
      </thead>
      <tbody class="h-full overflow-y-auto relative block custom-scroll">
        <vue-element-loading
          :active="loadingRows"
          spinner="spinner"
          duration="1.5"
          color="#4f4f4f"
          size="50"
        />
        <row-table
          v-for="(folder, index) in folderListItems"
          :folder-item="folder"
          :key="index"
          :has-details="hasDetails"
          :icon-main="iconMain"
        />
      </tbody>
      <tfoot class="border-t-2 border-zt-dark-200">
        <tr class="h-20 flex justify-end items-center text-zt-md px-4">
          <td class="flex items-center">
            <p class="mr-3">{{ $t('message.abm.folders.table.perPage') }}</p>
            <div class="select">
              <select
                name="cantPages"
                id="cantPages"
                @change="perPage"
                v-model="optionPerPageSelected"
              >
                <option v-for="option in optionsPerPages" :value="option.value">
                  {{ option.text }}
                </option>
              </select>
            </div>
            <div class="mr-4 ml-4">{{ text }}</div>
            <div class="font-weight w-16 flex justify-between">
              <button :disabled="isPrevDisabled" @click="gotoPreviousPage">
                <zt-icon
                  name="angle-left"
                  :class="{ 'cursor-pointer': !isPrevDisabled }"
                />
              </button>
              <button :disabled="isNextDisabled" @click="gotoNextPage">
                <zt-icon
                  name="angle-right"
                  :class="{ 'cursor-pointer': !isNextDisabled }"
                />
              </button>
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
import VueElementLoading from 'vue-element-loading'
import RowTable from './RowTable'
import { assign } from 'lodash'
export default {
  name: 'ListTable',
  components: { RowTable, VueElementLoading },
  props: {
    folderListItems: {
      default: () => [],
      type: Array
    },
    itemsPerPage: {
      type: Number,
      default: 10
    },
    currentPage: {
      type: Number,
      default: 1
    },
    totalRecords: {
      type: Number,
      default: 0
    },
    paginationText: {
      type: String,
      default: '{currentPage}-{perPage} {of} {totalRecords}'
    },
    hasDetails: {
      type: Boolean,
      default: true
    },
    columnHeader: {
      type: Array,
      default: () => []
    },
    iconMain: {
      type: String,
      default: 'folder'
    },
    loadingRows: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      pagination: {
        currentPage: 0,
        perPage: 0
      },
      optionsPerPages: [
        { text: '10', value: 10 },
        { text: '25', value: 25 },
        { text: '50', value: 50 }
      ]
    }
  },
  computed: {
    lastPage() {
      return Math.ceil(this.totalRecords / this.itemsPerPage)
    },
    text() {
      return this.paginationText
        .replace('{currentPage}', this.pagination.currentPage)
        .replace('{perPage}', this.pagination.perPage)
        .replace('{of}', this.$t('message.publishedEforms.paginator.of'))
        .replace('{totalRecords}', this.totalRecords)
    },
    isNextDisabled() {
      return this.pagination.currentPage === this.lastPage
    },
    isPrevDisabled() {
      return this.pagination.currentPage === 1
    },
    optionPerPageSelected() {
      return this.itemsPerPage
    }
  },
  created() {
    this.pagination = assign({}, this.pagination, {
      currentPage: this.currentPage,
      perPage: this.itemsPerPage
    })
  },
  methods: {
    gotoPreviousPage() {
      if (this.pagination.currentPage > 1 && !this.loadingRows) {
        const currentPage = this.pagination.currentPage - 1
        this.pagination = assign({}, this.pagination, { currentPage })
      }
    },
    gotoNextPage() {
      if (this.pagination.currentPage < this.lastPage && !this.loadingRows) {
        const currentPage = this.pagination.currentPage + 1
        this.pagination = assign({}, this.pagination, { currentPage })
      }
    },
    perPage(e) {
      const perPage = parseInt(e.target.value)
      const currentPage = 1
      this.pagination = assign({}, this.pagination, {
        currentPage,
        perPage
      })
    }
  },
  watch: {
    pagination: {
      handler() {
        this.$emit('paginate-change', this.pagination)
      },
      deep: true
    }
  }
}
</script>

<style scoped lang="scss">
.height-table {
  height: calc(100% - 80px);
}
.custom-scroll::-webkit-scrollbar-track {
  background-color: #c4c4c4;
}
.custom-scroll::-webkit-scrollbar {
  width: 0.625rem;
}
.custom-scroll::-webkit-scrollbar-thumb {
  border-radius: 5px;
  background-color: #888888;
}
button:disabled {
  opacity: 0.5;
  cursor: default;
}
.select {
  position: relative;
  select {
    width: 35px;
    height: 20px;
    appearance: none;
    background-image: url(data:image/svg+xml;base64,PHN2ZyBmaWxsPSdibGFjaycgaGVpZ2h0PScyNCcgdmlld0JveD0nMCAwIDI0IDI0JyB3aWR0aD0nMjQnIHhtbG5zPSdodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zyc+PHBhdGggZD0nTTcgMTBsNSA1IDUtNXonLz48cGF0aCBkPSdNMCAwaDI0djI0SDB6JyBmaWxsPSdub25lJy8+PC9zdmc+);
    background-repeat: no-repeat;
    background-position-x: 100%;
    background-position-y: -2px;
  }
  select:focus-visible {
    border: 0 none;
    outline: none;
  }
}
</style>
